import styles from './../_songPage.module.scss'
import songStyles from '../../../../components/_song.module.scss'

import artists from '../../../../utils/songs/artists'
import { albumPath } from '../../../../utils/routeHelpers'
import { CSSProperties, useContext, useEffect, useState } from 'react'
import SongVideo from './SongVideo'
import { t } from '../../../../utils'
import { PlayerContext } from '../../../../utils/song_player'
import AddToSetList from '../../../../components/AddToSetList'
import FavoriteSong from '../../../../components/FavoriteSong'
import classNames from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPause, faPlay } from '@fortawesome/free-solid-svg-icons'
import { useAuth } from '../../../../utils/auth'
import Image from 'next/legacy/image'
import { Song } from '../../../../__generated__/graphql'

const SongHeader: React.FC<{
  song: Song
  isPlaying: boolean
  setIsPlaying: (value: boolean) => void
  mixerIsPlaying: boolean
}> = ({ song, setIsPlaying, isPlaying, mixerIsPlaying }) => {
  const {
    playerState: { player, isLoading },
    playOrPause,
    stop
  } = useContext(PlayerContext)

  const playerSong = { id: song.id, url: song.sampleUrl }

  const { isAuthenticated, isLoading: isAuthLoading } = useAuth()

  const [hover, setHover] = useState(false)
  // Gate only the Wistia player for authenticated users — song details and
  // guest demo preview stay visible for LCP (not blocked on auth).
  const [isPlayerReady, setIsPlayerReady] = useState(false)

  useEffect(() => {
    if (isAuthLoading) return
    if (!isAuthenticated) setIsPlayerReady(true)
  }, [isAuthenticated, isAuthLoading])

  const hasPlayButton =
    player.isPaused(playerSong) || !player.isActiveSong(playerSong)

  const button = () => {
    if (player.isActiveSong(playerSong) && isLoading) return '...'

    if (hasPlayButton && !isPlaying) {
      return <FontAwesomeIcon icon={faPlay} />
    } else {
      return <FontAwesomeIcon icon={faPause} />
    }
  }

  useEffect(() => {
    if (mixerIsPlaying) {
      stop()
    }
  }, [mixerIsPlaying])

  useEffect(() => {
    setInterval(() => {
      if (!player.ensureIsPlaying()) {
        setIsPlaying(false)
      }
    }, 1000)
  }, [])

  // While auth is still loading, keep the player visible so guest demo/LCP
  // is not delayed. Hide only after we know the user is authenticated and
  // Wistia has not reported ready yet.
  const showPlayer = isAuthLoading || !isAuthenticated || isPlayerReady
  const playerStyle = {
    opacity: showPlayer ? 'initial' : 0
  } as CSSProperties

  return (
    <article className={styles.songArticle}>
      <div className="columns">
        <div className="column is-one-third">
          <div className={styles.songDetails}>
            <article
              onMouseOver={() => setHover(true)}
              onMouseOut={() => setHover(false)}
              className={classNames(songStyles.song, {
                [songStyles.isHovered]: hover
              })}
            >
              <div className={`songContainer ${songStyles.songContainer}`}>
                <figure>
                  <div className="is-square">
                    <div className={styles.songAvatar}>
                      <Image
                        src={song.album.cover.medium}
                        alt={song.title}
                        layout="fill"
                        width={280}
                        height={280}
                      />
                    </div>
                    {isAuthenticated && (
                      <ul className={songStyles.songActions}>
                        <li>
                          <AddToSetList song={song} />
                        </li>
                        <li>
                          <FavoriteSong song={song} />
                        </li>
                      </ul>
                    )}
                  </div>

                  <figcaption>
                    <button
                      className={songStyles.play}
                      onClick={() => {
                        playOrPause(playerSong)
                        setIsPlaying(!isPlaying)
                      }}
                    >
                      {button()}
                    </button>
                  </figcaption>
                </figure>
              </div>
            </article>

            <div className={styles.songDetailsDescription}>
              <h1>{song.title}</h1>
              <h2>
                {artists(song)} |{' '}
                <a href={albumPath(song.album)}>{song.album.name}</a>
              </h2>

              <table>
                <tbody>
                  <tr>
                    <td>{t('KEY')}</td>
                    <td>
                      <span className={styles.keyLabel}>{song.key}</span>
                    </td>
                  </tr>
                  <tr>
                    <td>{t('BPM')}</td>
                    <td>{song.bpm}</td>
                  </tr>
                  <tr>
                    <td>{t('TIME SIG')}</td>
                    <td>{song.timeSignature}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <SongVideo
          song={song}
          setIsPlaying={setIsPlaying}
          setIsPlayerReady={setIsPlayerReady}
          mixerIsPlaying={mixerIsPlaying}
          playerStyle={playerStyle}
        />
      </div>
    </article>
  )
}

export default SongHeader
